import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { Link } from 'react-router';

import { LinkWithSrc } from '~/components/link-with-src';
import ThumbnailWithFallback from '~/components/photos/thumbnail-with-fallback';
import { Button } from '~/components/ui/button';
import { Icon } from '~/components/ui/icon';
import { type EventResult } from '~/domain/events/models/event-result';
import { THUMBNAIL_SIZE } from '~/domain/shared/models/thumbnail-size';
import { parseDate, parseTime } from '~/domain/shared/time-helpers';
import { cn } from '~/styles/extended-theme';

import { EventAddress } from './event-address';

interface EventCardProps {
  className?: string;
  event: EventResult;
  includeDetailLink?: boolean;
  imageLayout?: 'small' | 'large';
  includeGymLink?: boolean;
}

type EventCardDetailProps = Omit<EventCardProps, 'imageLayout'>;

export const formatDate = (date: string) => format(parseDate(date), 'EEEE, MMMM d, yyyy');

export const formatTime = (time: string | Date) => format(time, 'h:mm aa');

function CardRow({
  icon,
  className,
  children,
  breakWords = false,
}: React.PropsWithChildren<{
  icon: React.ComponentProps<typeof Icon>['name'];
  className?: string;
  breakWords?: boolean;
}>) {
  return (
    <span
      className={cn(
        'flex min-h-6 w-full items-center gap-2 text-body1',
        breakWords ? 'line-clamp-2 flex overflow-ellipsis whitespace-break-spaces break-all' : null,
        className,
      )}
    >
      <Icon name={icon} className="mt-1 flex-shrink-0 self-start" />
      {children}
    </span>
  );
}

function EventInfo({
  event,
  linkPresenter,
  includeGymLink,
}: {
  event: EventResult;
  linkPresenter?: boolean;
  includeGymLink?: boolean;
}) {
  const gymUrl = event.gym.urlSlug ? `/gym/${event.gym.urlSlug}` : `/gym/${event.gym.id}`;
  return (
    <div className="flex flex-1 flex-col gap-1">
      <h6>{event.name}</h6>
      <CardRow icon="calendar">{formatDate(event.date)}</CardRow>
      <CardRow icon="clock">
        {formatTime(parseTime(event.startTime))}–{formatTime(parseTime(event.endTime))}
      </CardRow>
      {event.presenter?.name ? (
        <CardRow icon="avatar" className="text-info" breakWords>
          {linkPresenter && event.presenter.id ? (
            <Link to={`/a/${event.presenter.id}`} className="text-inherit decoration-inherit">
              {event.presenter.name}
            </Link>
          ) : (
            event.presenter.name
          )}
        </CardRow>
      ) : null}
      <CardRow icon="gi" breakWords>
        {includeGymLink ? (
          <Link to={gymUrl} className="underline">
            {event.gym.name}
          </Link>
        ) : (
          event.gym.name
        )}
      </CardRow>
      <CardRow icon="sewing-pin-filled">
        <EventAddress location={event.location} />
      </CardRow>
    </div>
  );
}

export function EventCard({ imageLayout = 'small', ...props }: EventCardProps) {
  return imageLayout === 'small' ? (
    <SmallImageEventCard {...props} />
  ) : (
    <LargeImageEventCard {...props} />
  );
}

function SmallImageEventCard({ className, event, includeGymLink }: EventCardDetailProps) {
  return (
    <div className={cn('bg-white p-6 shadow-md', className)}>
      <div className="flex justify-between gap-2">
        <EventInfo event={event} includeGymLink={includeGymLink} />
        <div className="flex flex-col items-center gap-2">
          {event.gymPhotoUrl ? (
            <ThumbnailWithFallback
              imageClassName="size-auto max-w-24 max-h-24"
              size={THUMBNAIL_SIZE.SQUARE_EXTRA_SMALL}
              url={event.gymPhotoUrl}
              rounded
            />
          ) : null}
          {event.presenter?.thumbnailUrl ? (
            <ThumbnailWithFallback
              imageClassName="size-auto max-w-20 max-h-20"
              size={THUMBNAIL_SIZE.SQUARE_EXTRA_SMALL}
              url={event.presenter.thumbnailUrl}
              rounded
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LargeImageEventCard({
  className,
  event,
  includeDetailLink,
  includeGymLink,
}: EventCardDetailProps) {
  return (
    <div className={cn('flex flex-col gap-3 bg-white shadow-md', className)}>
      {event.thumbnailUrl ? (
        <div className="flex items-center justify-center bg-black">
          <ThumbnailWithFallback
            imageClassName="size-auto max-h-44 w-full object-cover"
            size={THUMBNAIL_SIZE.GYM_EVENT}
            url={event.thumbnailUrl}
          />
        </div>
      ) : null}
      <div className="flex gap-1 px-4">
        <EventInfo event={event} linkPresenter includeGymLink={includeGymLink} />
        {event.presenter?.thumbnailUrl ? (
          <ThumbnailWithFallback
            imageClassName="size-auto max-w-24 max-h-24"
            size={THUMBNAIL_SIZE.SQUARE_SMALL}
            url={event.presenter.thumbnailUrl}
          />
        ) : null}
      </div>
      {includeDetailLink ? (
        <div className="flex items-center justify-center p-4">
          <Button asChild variant="outline">
            <LinkWithSrc to={{ pathname: `/events/${event.id}` }}>
              View Details
              <ExternalLinkIcon className="ml-2 h-4 w-4" />
            </LinkWithSrc>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
